import React, { createContext, useContext, useState, ReactNode } from 'react';
import { MusicData } from '@/components/cards/MusicCard';

interface MusicPlayerContextType {
  currentMusic: MusicData | null;
  isPlayerOpen: boolean;
  currentSongIndex: number;
  playMusic: (music: MusicData, songIndex?: number) => void;
  closePlayer: () => void;
  // Additional properties for SoundCard compatibility
  currentTrack?: { id: string };
  isPlaying?: boolean;
  togglePlayPause?: () => void;
  setCurrentTrack?: (track: any) => void;
}

const MusicPlayerContext = createContext<MusicPlayerContextType | undefined>(undefined);

export const MusicPlayerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentMusic, setCurrentMusic] = useState<MusicData | null>(null);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const playMusic = (music: MusicData, songIndex = 0) => {
    setCurrentMusic(music);
    setCurrentSongIndex(songIndex);
    setIsPlayerOpen(true);
    setIsPlaying(true);
  };

  const togglePlayPause = () => {
    setIsPlaying(prev => !prev);
  };

  const setCurrentTrack = (track: any) => {
    // Convert track format to MusicData if needed
    const musicData: MusicData = {
      id: track.id,
      artist: track.artist || '',
      album: track.title || '',
      title: track.title || '',
      cover_url: track.artwork || '',
      artist_avatar: track.artwork || '',
      verified: false,
      plays: '0',
      genre: 'Sound',
      releaseDate: new Date().toLocaleDateString(),
      songs: [
        {
          id: `${track.id}-1`,
          title: track.title || 'Untitled Sound',
          featuring: '',
          duration: '0:30'
        }
      ]
    };

    setCurrentMusic(musicData);
    setCurrentSongIndex(0);
    setIsPlayerOpen(true);
    setIsPlaying(true);
  };

  const closePlayer = () => {
    setIsPlayerOpen(false);
  };

  return (
    <MusicPlayerContext.Provider
      value={{
        currentMusic,
        isPlayerOpen,
        currentSongIndex,
        playMusic,
        closePlayer,
        // Additional properties for SoundCard compatibility
        currentTrack: currentMusic ? { id: currentMusic.id } : undefined,
        isPlaying,
        togglePlayPause,
        setCurrentTrack
      }}
    >
      {children}
    </MusicPlayerContext.Provider>
  );
};

export const useMusicPlayer = (): MusicPlayerContextType => {
  const context = useContext(MusicPlayerContext);
  if (context === undefined) {
    throw new Error('useMusicPlayer must be used within a MusicPlayerProvider');
  }
  return context;
};
